<template>
  <div>
            <div v-if="showQuarantineModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[140] backdrop-blur-sm" style="z-index:10010;">
                <div class="card shadow-xl w-full max-w-5xl flex flex-col max-h-[90vh] overflow-hidden">
                    <div class="flex justify-between items-center p-4 border-b bg-white">
                        <h3 class="font-extrabold text-lg text-slate-900">
                            {{ quarantineMode === 'phishing' ? 'AI 詐騙隔離區' : '垃圾郵件隔離區' }}
                        </h3>
                        <button @click="closeQuarantineModal" class="text-xl" style="color:var(--gray-500);">×</button>
                    </div>
                    <div class="p-4 border-b flex flex-wrap gap-3 items-center" style="background:var(--gray-50);">
                        <div class="flex items-center gap-2">
                            <span class="text-xs font-bold" style="color:var(--gray-600);">資料夾</span>
                            <select v-model="quarantineFolder" class="input text-xs min-w-[220px]">
                                <option v-for="f in quarantineFolders" :key="f" :value="f">{{ f }}</option>
                            </select>
                        </div>
                        <div class="flex items-center gap-2">
                            <span class="text-xs font-bold" style="color:var(--gray-600);">掃描</span>
                            <input v-model.trim="quarantineScanFolder" type="text"
                                class="input text-xs w-28" placeholder="INBOX" />
                        </div>
                        <div class="flex items-center gap-2">
                            <span class="text-xs font-bold" style="color:var(--gray-600);">筆數</span>
                            <input v-model.number="quarantineScanLimit" type="number" min="1" max="500" step="10"
                                class="input text-xs w-24" />
                        </div>
                        <div class="flex items-center gap-2">
                            <span class="text-xs font-bold" style="color:var(--gray-600);">門檻</span>
                            <input v-if="quarantineMode === 'phishing'" v-model.number="quarantineScanPhishingThreshold"
                                type="number" min="0" max="1" step="0.05" class="input text-xs w-24" />
                            <input v-else v-model.number="quarantineScanSpamThreshold"
                                type="number" min="0" step="0.5" class="input text-xs w-24" />
                        </div>
                        <label class="flex items-center gap-2 text-xs font-bold select-none" style="color:var(--gray-600);">
                            <input type="checkbox" v-model="quarantineScanUnseenOnly" />
                            只掃未讀
                        </label>
                        <label class="flex items-center gap-2 text-xs font-bold select-none" style="color:var(--gray-600);">
                            <input type="checkbox" v-model="quarantineShowAnalysis" @change="fetchQuarantineList(true)" />
                            顯示原因/分析
                        </label>
                        <div class="flex items-center gap-2 flex-1 min-w-[240px]">
                            <span class="text-xs font-bold" style="color:var(--gray-600);">搜尋</span>
                            <input v-model.trim="quarantineSearch" @keyup.enter="fetchQuarantineList(true)" type="text"
                                class="input text-xs w-full" placeholder="主旨 / 寄件者關鍵字" />
                        </div>
                        <div v-if="quarantineMode === 'phishing'" class="flex items-center gap-2">
                            <span v-if="quarantineCacheInfo && quarantineCacheInfo.size !== undefined" class="text-[11px]" style="color:var(--gray-500);">
                                快取 {{ Number(quarantineCacheInfo.size || 0) }}
                            </span>
                            <button @click="fetchPhishingQuarantineCache" class="btn btn-xs btn-outline" :disabled="quarantineLoading">
                                快取狀態
                            </button>
                            <button @click="clearPhishingQuarantineCache" class="btn btn-xs btn-outline" :disabled="quarantineLoading">
                                清空快取
                            </button>
                        </div>
                        <button @click="scanQuarantineSourceFolder"
                            :disabled="quarantineLoading || quarantineSeedingExamples || quarantineScanning"
                            class="btn btn-xs btn-warning text-white">
                            <span v-if="quarantineScanning" class="animate-spin mr-1">⟳</span> AI 掃描並隔離
                        </button>
                        <button @click="fetchQuarantineList(true)" :disabled="quarantineLoading"
                            class="btn btn-xs btn-outline">
                            <span v-if="quarantineLoading" class="animate-spin mr-1">⟳</span> 重新整理
                        </button>
                        <button @click="seedQuarantineExamples"
                            :disabled="quarantineLoading || quarantineSeedingExamples"
                            class="btn btn-xs btn-outline">
                            <span v-if="quarantineSeedingExamples" class="animate-spin mr-1">⟳</span> 加入範例
                        </button>
                        <button @click="quarantineAddSelectedToMap('allow')"
                            :disabled="quarantineLoading || quarantineMapLoading || !isSuperAdmin"
                            class="btn btn-xs btn-outline"
                            :title="(quarantineSelectedIds || []).length ? '將已選信件的寄件者加入名單' : '開啟名單管理（未勾選信件時）'">
                            {{ quarantineMode === 'phishing' ? '信任名單' : '允許名單' }}
                        </button>
                        <button @click="quarantineAddSelectedToMap('block')"
                            :disabled="quarantineLoading || quarantineMapLoading || !isSuperAdmin"
                            class="btn btn-xs btn-outline"
                            :title="(quarantineSelectedIds || []).length ? '將已選信件的寄件者加入名單' : '開啟名單管理（未勾選信件時）'">
                            封鎖名單
                        </button>
                        <button @click="quarantineAddSelectedDomainsToMap('allow')"
                            :disabled="quarantineLoading || quarantineMapLoading || !isSuperAdmin"
                            class="btn btn-xs btn-outline"
                            :title="(quarantineSelectedIds || []).length ? '將已選信件的寄件網域加入名單（以 @網域 形式）' : '開啟名單管理（未勾選信件時）'">
                            {{ quarantineMode === 'phishing' ? '信任網域' : '允許網域' }}
                        </button>
                        <button @click="quarantineAddSelectedDomainsToMap('block')"
                            :disabled="quarantineLoading || quarantineMapLoading || !isSuperAdmin"
                            class="btn btn-xs btn-outline"
                            :title="(quarantineSelectedIds || []).length ? '將已選信件的寄件網域加入名單（以 @網域 形式）' : '開啟名單管理（未勾選信件時）'">
                            封鎖網域
                        </button>
                    </div>
                    <div class="p-4 flex-1 overflow-y-auto bg-white">
                        <div v-if="quarantineError" class="mb-3 p-3 rounded border bg-rose-50 text-rose-800 text-sm">
                            {{ quarantineError }}
                        </div>
                        <div v-if="quarantineBatch && quarantineBatch.running" class="mb-3 p-3 rounded border bg-slate-50 text-slate-700 text-sm flex items-center justify-between gap-3">
                            <div class="font-bold">
                                批次處理中：{{ quarantineBatch.action }}（{{ Number(quarantineBatch.done || 0) }}/{{ Number(quarantineBatch.total || 0) }}）
                            </div>
                            <div class="text-xs text-slate-500" v-if="quarantineBatch.errors && quarantineBatch.errors.length">
                                錯誤 {{ quarantineBatch.errors.length }} 筆
                            </div>
                        </div>
                        <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-3 text-sm" style="color:var(--gray-600);">
                            <div class="flex flex-wrap items-center gap-3">
                                <div>共 {{ quarantineTotal || 0 }} 封</div>
                                <div v-if="(quarantineSelectedIds || []).length" class="text-slate-700 font-bold">
                                    已選 {{ (quarantineSelectedIds || []).length }} 封
                                </div>
                                <button v-if="(quarantineSelectedIds || []).length" @click="clearQuarantineSelection" class="btn btn-xs btn-outline" :disabled="quarantineLoading || (quarantineBatch && quarantineBatch.running)">
                                    清空勾選
                                </button>
                                <button v-if="(quarantineSelectedIds || []).length" @click="batchReleaseQuarantineSelected('release')" class="btn btn-xs btn-success text-white" :disabled="quarantineLoading || (quarantineBatch && quarantineBatch.running)">
                                    批次釋放
                                </button>
                                <button v-if="(quarantineSelectedIds || []).length" @click="batchReleaseQuarantineSelected('allowlist')" class="btn btn-xs btn-primary text-white" :disabled="quarantineLoading || (quarantineBatch && quarantineBatch.running)">
                                    {{ quarantineMode === 'phishing' ? '批次信任' : '批次允許' }}
                                </button>
                                <button v-if="(quarantineSelectedIds || []).length" @click="batchReleaseQuarantineSelected('allowlist_domain')" class="btn btn-xs btn-outline" :disabled="quarantineLoading || (quarantineBatch && quarantineBatch.running) || !isSuperAdmin">
                                    {{ quarantineMode === 'phishing' ? '批次信任網域' : '批次允許網域' }}
                                </button>
                                <button v-if="(quarantineSelectedIds || []).length" @click="batchReleaseQuarantineSelected('delete')" class="btn btn-xs btn-error text-white" :disabled="quarantineLoading || (quarantineBatch && quarantineBatch.running)">
                                    批次刪除
                                </button>
                            </div>
                            <div class="flex items-center gap-2">
                                <button @click="quarantinePrevPage" class="btn btn-xs btn-outline" :disabled="quarantineLoading || quarantineOffset <= 0">上一頁</button>
                                <button @click="quarantineNextPage" class="btn btn-xs btn-outline" :disabled="quarantineLoading || (quarantineOffset + quarantineLimit) >= (quarantineTotal || 0)">下一頁</button>
                            </div>
                        </div>

                        <div class="overflow-x-auto border rounded">
                            <table class="data-table">
                                <thead>
                                    <tr>
                                        <th class="p-2 w-10">
                                            <input type="checkbox"
                                                :checked="isQuarantineAllPageSelected()"
                                                @change="toggleQuarantineSelectAllPage"
                                                :disabled="quarantineLoading || !quarantineItems || quarantineItems.length === 0 || (quarantineBatch && quarantineBatch.running)" />
                                        </th>
                                        <th class="p-2 w-44">時間</th>
                                        <th class="p-2 w-64">寄件者</th>
                                        <th class="p-2">主旨</th>
                                        <th class="p-2 w-72">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="!quarantineLoading && (!quarantineItems || quarantineItems.length === 0)">
                                        <td colspan="5" class="p-4 text-center" style="color:var(--gray-500);">沒有資料</td>
                                    </tr>
                                    <tr v-for="m in (quarantineItems || [])" :key="m.id" class="border-b">
                                        <td class="p-2">
                                            <input type="checkbox"
                                                :checked="isQuarantineSelected(m.id)"
                                                @change="toggleQuarantineSelect(m.id)"
                                                :disabled="quarantineLoading || (quarantineBatch && quarantineBatch.running)" />
                                        </td>
                                        <td class="p-2 whitespace-nowrap" style="color:var(--gray-600);">{{ m.date || '—' }}</td>
                                        <td class="p-2 font-mono break-all">{{ m.from || '—' }}</td>
                                        <td class="p-2 break-words">
                                            <div>
                                                <span>{{ m.subject || '(無主旨)' }}</span>
                                                <span v-if="quarantineMode === 'phishing' && (m.phishing_score !== undefined && m.phishing_score !== null)"
                                                    class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700">
                                                    {{ Number(m.phishing_score || 0).toFixed(2) }}
                                                </span>
                                                <span v-else-if="quarantineMode !== 'phishing' && (m.spam_score !== undefined && m.spam_score !== null)"
                                                    class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                                                    {{ Number(m.spam_score || 0).toFixed(1) }}
                                                </span>
                                            </div>
                                            <div v-if="quarantineReasonText(m)" class="mt-1 text-[11px]" style="color:var(--gray-500);">
                                                {{ quarantineReasonText(m) }}
                                            </div>
                                        </td>
                                        <td class="p-2">
                                            <div class="flex flex-wrap gap-2">
                                                <button @click="openQuarantineMessage(m)"
                                                    class="btn btn-xs btn-outline"
                                                    :disabled="quarantineActionLoadingId === m.id">
                                                    查看
                                                </button>
                                                <button @click="quarantineAddMessageToMap(m, 'allow')"
                                                    class="btn btn-xs btn-outline"
                                                    :disabled="quarantineActionLoadingId === m.id || quarantineMapLoading || !isSuperAdmin">
                                                    {{ quarantineMode === 'phishing' ? '加入信任' : '加入允許' }}
                                                </button>
                                                <button @click="quarantineAddMessageDomainToMap(m, 'allow')"
                                                    class="btn btn-xs btn-outline"
                                                    :disabled="quarantineActionLoadingId === m.id || quarantineMapLoading || !isSuperAdmin">
                                                    {{ quarantineMode === 'phishing' ? '加入信任網域' : '加入允許網域' }}
                                                </button>
                                                <button @click="quarantineAddMessageToMap(m, 'block')"
                                                    class="btn btn-xs btn-outline"
                                                    :disabled="quarantineActionLoadingId === m.id || quarantineMapLoading || !isSuperAdmin">
                                                    加入封鎖
                                                </button>
                                                <button @click="quarantineAddMessageDomainToMap(m, 'block')"
                                                    class="btn btn-xs btn-outline"
                                                    :disabled="quarantineActionLoadingId === m.id || quarantineMapLoading || !isSuperAdmin">
                                                    加入封鎖網域
                                                </button>
                                                <button @click="releaseQuarantineMessage(m)"
                                                    class="btn btn-xs btn-success text-white"
                                                    :disabled="quarantineActionLoadingId === m.id">
                                                    釋放到收件匣
                                                </button>
                                                <button @click="releaseQuarantineAllowlistMessage(m)"
                                                    class="btn btn-xs btn-primary text-white"
                                                    :disabled="quarantineActionLoadingId === m.id">
                                                    {{ quarantineMode === 'phishing' ? '釋放並加入信任' : '釋放並加入允許' }}
                                                </button>
                                                <button @click="releaseQuarantineAllowlistDomainMessage(m)"
                                                    class="btn btn-xs btn-outline"
                                                    :disabled="quarantineActionLoadingId === m.id || quarantineMapLoading || !isSuperAdmin">
                                                    {{ quarantineMode === 'phishing' ? '釋放並加入信任網域' : '釋放並加入允許網域' }}
                                                </button>
                                                <button @click="deleteQuarantineMessage(m)"
                                                    class="btn btn-xs btn-error text-white"
                                                    :disabled="quarantineActionLoadingId === m.id">
                                                    刪除
                                                </button>
                                                <button @click="downloadQuarantineEml(m)"
                                                    class="btn btn-xs btn-outline"
                                                    :disabled="quarantineActionLoadingId === m.id">
                                                    下載 eml
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="p-4 border-t flex justify-end bg-slate-50">
                        <button @click="closeQuarantineModal" class="btn btn-sm btn-outline">關閉</button>
                    </div>
                </div>
            </div>

            <div v-if="showQuarantineMapModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[150] backdrop-blur-sm" style="z-index:10020;">
                <div class="card shadow-xl w-full max-w-3xl flex flex-col max-h-[90vh] overflow-hidden">
                    <div class="flex justify-between items-center p-4 border-b bg-white">
                        <h3 class="font-extrabold text-lg text-slate-900">{{ quarantineMapTitle() }}</h3>
                        <button @click="closeQuarantineMapModal" class="text-xl" style="color:var(--gray-500);">×</button>
                    </div>
                    <div class="p-4 bg-white flex-1 overflow-y-auto">
                        <div class="text-xs mb-2" style="color:var(--gray-500);">每行輸入一個寄件者（user@domain.com）或網域（@domain.com）</div>
                        <textarea v-model="quarantineMapText" class="input p-2 font-mono" style="min-height:360px;"></textarea>
                        <div v-if="quarantineMapError" class="mt-2 text-sm text-rose-700">{{ quarantineMapError }}</div>
                    </div>
                    <div class="p-4 border-t bg-slate-50 flex justify-end gap-2">
                        <button @click="saveQuarantineMapModal" class="btn btn-sm btn-primary text-white" :disabled="quarantineMapLoading">
                            {{ quarantineMapLoading ? '儲存中...' : '儲存' }}
                        </button>
                        <button @click="closeQuarantineMapModal" class="btn btn-sm btn-outline">關閉</button>
                    </div>
                </div>
            </div>

  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()
const stateRefs = storeToRefs(store)

// Spread all commonly needed state refs
const {
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  invoiceDriveState,
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  sslCerts, sslLoading, sslDomain,
  securityWizardStep, spamStats, securityFilterState,
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  ragState, aiSalesState, aiMarketingState,
  lineSupportState,
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  unrepliedEmails, unrepliedLoading,
  cbiInsights, cbiLoading,
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  showQuarantineModal, showQuarantineMapModal,
  quarantineMode, quarantineFolder, quarantineFolders,
  quarantineScanFolder, quarantineScanLimit,
  quarantineScanPhishingThreshold, quarantineScanSpamThreshold,
  quarantineLoading, quarantineItems, quarantineTotal,
  quarantineMapText, quarantineMapError, quarantineMapLoading,
  isSuperAdmin, isDomainAdmin,
  quarantineOffset, quarantineLimit, quarantineSearch, quarantineShowAnalysis,
  quarantineScanUnseenOnly, quarantineCacheInfo, quarantineScanning, quarantineSeedingExamples,
  quarantineSelectedIds, quarantineBatch, quarantineActionLoadingId,
} = stateRefs

// Methods accessed directly from store
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied, fetchUnrepliedMessages, followUpUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  openQuarantineModal, closeQuarantineModal, scanQuarantine, releaseQuarantineItem, deleteQuarantineItem,
  openQuarantineMapModal, closeQuarantineMapModal, saveQuarantineMapModal,
  openNotificationsModal, refreshAiSales, scanInboundReplies,
  autoRepliesScanLoading,
  fetchQuarantineList, clearQuarantineSelection, isQuarantineSelected, toggleQuarantineSelect,
  toggleQuarantineSelectAllPage, isQuarantineAllPageSelected, batchReleaseQuarantineSelected,
  quarantinePrevPage, quarantineNextPage,
  fetchPhishingQuarantineCache, clearPhishingQuarantineCache,
  quarantineAddSelectedToMap, quarantineAddSelectedDomainsToMap,
} = store
</script>

